import React, {useEffect, useState} from 'react';

import './ProjectsEditing.scss';
import {AutocompleteMUI, ButtonMUI, IconButtonMUI} from '../../../shared';
import * as yup from 'yup';
import {Controller, useFieldArray, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import InputMUI from '../../../shared/InputMUI';
import SelectComponent from '../../../shared/SelectComponent';
import ProjectsHero from '../../Components/Projects/ProjectsHero';
import DropzoneAvatar from '../../Components/DropzoneAvatar';
import {POPUPS} from '../../../redux/actions/app/appActionTypes';
import {useDispatch, useSelector} from 'react-redux';
import {getCities, getCountries} from '../../../redux/actions/countries/countriesActions';
import {getCompanyEmployeeList} from '../../../redux/actions/employee/employeeActions';
import {getCompaniesClientsList} from '../../../redux/actions/clients/clientsActions';
import {
    editProject,
    getProject,
    getProjectMembersList,
    getProjects, getProjectsAvatars,
    getProjectTypes, projectRemove
} from '../../../redux/actions/projects/projectsActions';
import {notifyError, notifySuccess} from '../../../helpers/notifySnack';
import LoadPanel from '../../Components/LoadPanel';
import {ReactComponent as TrashIcon} from '../../../assets/icons/trash.svg';
import {ReactComponent as PlusIcon} from '../../../assets/icons/plus-icon.svg';
import {ReactComponent as RemoveIcon} from '../../../assets/icons/remove.svg';
import {useHistory, useParams} from 'react-router-dom';
import AvatarIcon from '../../../assets/images/companies/avatar.svg';
import {hasPermission} from '../../../helpers/functions';
import {PERMISSIONS} from '../../../helpers/constants';
import {useTranslation} from 'react-i18next';
import {showConfirmPopup} from "../../../redux/actions/app/appActions";
import {mainPath} from "../../../routes/paths";

const ProjectsEditing = () => {
    const [submitted, setSubmitted] = useState(false);
    const dispatch = useDispatch();
    const {singleProject} = useSelector((state) => state.projects);
    const {companies, companiesLoading} = useSelector((state) => state.company);
    const {countriesLoading, countries, cities, citiesLoading} = useSelector(({countries}) => countries);
    const {companyEmployeesList, companyEmployeesListLoading} = useSelector(({employee}) => employee);
    const {companiesClientsList, companiesClientsListLoading} = useSelector(({clients}) => clients);
    const {projectTypes, projectTypesLoading, avatars} = useSelector(({projects}) => projects);
    const [filteredCities, setFilteredCities] = useState([]);
    const {profileMe} = useSelector((state) => state.profile);
    const {t} = useTranslation();
    const history = useHistory()
    const {id} = useParams();
    const removePermission = hasPermission(PERMISSIONS.PROJECT_DELETE, profileMe?.permissions ?? []);
    const allowActions = singleProject?.status === 'active' || singleProject?.status === 'pause'

    const schema = yup.object().shape({
        company: yup.string().required(t('ProjectsEditing.company_required')),
        city: yup.string().required(t('ProjectsEditing.city_required')),
        country: yup.string().required(t('ProjectsEditing.country_required')),
        address: yup.string().required(t('ProjectsEditing.address_required')),
        projects: yup
            .array()
            .of(
                yup.object().shape({
                    project: yup.string().required(t('ProjectsEditing.project_required'))
                })
            )
            .required(t('ProjectsEditing.project_required'))
    });

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: {errors, isValid, isSubmitting}
    } = useForm({
        mode: 'all',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        resolver: yupResolver(schema),
        defaultValues: {
            company: "",
            projects: [{project: ''}],
            city: '',
            country: '',
            address: '',
            employee: [],
            client: [],
            logo: null
        }
    });

    const {fields, append, remove} = useFieldArray({
        control,
        name: 'projects'
    });

    const onFilesAdded = (avatar) => {
        setValue('logo', avatar);
    };
    const onSubmit = async (data) => {
        const formData = new FormData();

        formData.append('cityId', data.city);
        formData.append('companyId', data.company);
        formData.append('country', data.country);
        formData.append('address', data.address);

        data.employee.forEach((employee, index) => {
            formData.append(`employees[${index}]`, employee.id);
        });

        data.clients.forEach((client, index) => {
            formData.append(`clients[${index}]`, client.id);
        });

        if (data.logo) {
            formData.append('logoId', data.logo);
        }

        data.projects?.forEach((item, index) => {
            formData.append(`typeIds[${index}]`, item.project);
        });

        const res = await dispatch(editProject(formData, id));

        if (res.payload && res.payload.status === 202) {
            await dispatch(getProjects(true));
            await dispatch(getProject(id, true));
            await dispatch(getProjectMembersList(singleProject.id));
            notifySuccess(t('all.successfully'));
        }
    };

    const employeeArr = companyEmployeesList.map((item) => {
        return {
            label: item.firstName ?? "",
            id: item.id,
            avatar: item.avatar?.thumbnail ?? item.avatar ?? null,
            text: item.roleName,
            isMe: profileMe?.id === item.id,
        };
    });

    const clientsArr = companiesClientsList.map((item) => {
        return {
            label: item.firstName ?? "",
            id: item.id,
            avatar: item.avatar?.thumbnail ?? item.avatar ?? null,
            text: item.roleName,
            isSpectator: item.role === 'spectator',
        };
    });

    const getFilteredOptions = (index) => {
        const selectedIds = projectsWatch.map((field) => field.project).filter((id) => id !== undefined);
        return projectTypes.filter((option) => !selectedIds.includes(option.id) || selectedIds[index] === option.id);
    };

    const projectsWatch = watch('projects');

    const handleCountryChange = (selectedCountryId) => {
        const filtered = cities.filter((city) => city.countryId === selectedCountryId);
        setFilteredCities(filtered);
    };

    useEffect(() => {
        const countryId = watch('country');
        handleCountryChange(countryId);
    }, [cities, watch('country')]);

    useEffect(() => {
        const fetchFunc = async () => {
            try {
                if (companiesClientsList && singleProject?.companyId) {
                    const clientsRes = await dispatch(getCompaniesClientsList(singleProject?.companyId));
                    if (clientsRes.error) {
                        return;
                    }
                }

                if (avatars?.loading) {
                    const projectAvatars = await dispatch(getProjectsAvatars());
                    if (projectAvatars.error) {
                        return;
                    }
                }

                if (projectTypesLoading) {
                    const projectTypesRes = await dispatch(getProjectTypes());
                    if (projectTypesRes.error) {
                        return;
                    }
                }

                if (companyEmployeesListLoading && singleProject?.companyId) {
                    const employeeRes = await dispatch(getCompanyEmployeeList(singleProject?.companyId));
                    if (employeeRes.error) {
                        return;
                    }
                }

                if (countriesLoading) {
                    const countriesRes = await dispatch(getCountries());
                    if (countriesRes.error) {
                        return;
                    }
                }

                if (citiesLoading) {
                    await dispatch(getCities());
                }
            } catch (error) {
                notifyError(error.message || 'error');
            }
        };
        fetchFunc();
    }, [dispatch]);

    useEffect(() => {
        if (singleProject && Object.keys(singleProject).length) {
            reset({
                address: singleProject.address || "",
                logo: singleProject.logo || null,
                company: singleProject.companyId || "",
                city: singleProject.city ? singleProject.city.id : "",
                country: singleProject.country ? singleProject.country.id : "",
                employee: singleProject.employees
                    ? singleProject.employees.map((item) => ({
                        label: item.firstName ?? "",
                        id: item.id,
                        avatar: item.avatar?.thumbnail ?? item.avatar ?? null,
                        isSpectator: item.role === 'spectator',
                        text: item.position,
                        isMe: profileMe?.id === item.id,
                    }))
                    : [],
                clients: singleProject.clients
                    ? singleProject.clients.map((item) => ({
                        label: item.firstName ?? "",
                        id: item.id,
                        avatar: item.avatar?.thumbnail ?? item.avatar ?? null,
                        text: item.position
                    }))
                    : [],
                projects:
                    singleProject.types && singleProject.types.length > 0
                        ? singleProject.types.map((type) => ({project: type.id}))
                        : [{project: ""}]
            });
        }
    }, [singleProject]);

    const permissionEdit = hasPermission(PERMISSIONS.PROJECT_EDIT, profileMe?.permissions ?? []) && allowActions;

    const avatarWatch = watch('logo');

    const removeHandler = async () => {
        if (!submitted) {
            setSubmitted(true);
            const res = await dispatch(projectRemove(id));

            if (res?.payload) {
                setSubmitted(false);
                const projectsRes = await dispatch(getProjects())
                if (projectsRes?.payload) {
                    history.push(mainPath.projects);
                    notifySuccess(t('all.project_deleted'));
                }
            } else {
                setSubmitted(false);
            }
        }

    }
    const removeHandlerRedux = () => {
        dispatch(
            showConfirmPopup({
                visible: true,
                title: t('all.projectRemoveTitle'),
                text: t('all.projectRemove'),
                confirmCallback: removeHandler
            })
        );
    };
    return (
        <div className={'projects__editing'}>
            {countriesLoading ||
            citiesLoading ||
            companyEmployeesListLoading ||
            projectTypesLoading ||
            avatars?.loading ||
            companiesLoading ||
            companiesClientsListLoading ? (
                <LoadPanel circleSize={40} staticPos={true} noBg={true}/>
            ) : (
                <>
                    {removePermission ? <ProjectsHero heading={t('ProjectsEditing.edit_project')} buttons={<>
                        <IconButtonMUI title={t('all.projectRemoveTitle')} onClick={removeHandlerRedux}>
                            <TrashIcon/>
                        </IconButtonMUI>
                    </>}/> : null}

                    <div className='projects__editing-scrollable'>
                        <div className='projects__editing-fields'>
                            <div className='projects__editing-dropzone'>
                                <DropzoneAvatar
                                    initialAvatarUrl={{
                                        avatar: singleProject?.logo,
                                        id: singleProject?.logoId,
                                    }}
                                    initialAvatar={AvatarIcon}
                                    onFileAdded={onFilesAdded}
                                    dropText={t('ProjectsEditing.select_logo')}
                                    images={avatars?.list || []}
                                    path={POPUPS.AVATAR_PROJECT_EDIT_POPUP}
                                    disabled={!permissionEdit}
                                />
                            </div>
                            <div className='projects__editing-input'>
                                <Controller
                                    name='company'
                                    control={control}
                                    render={({field}) => {
                                        return (
                                            <SelectComponent
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                value={field.value || ""}
                                                options={companies}
                                                label={t('ProjectsEditing.select_company')}
                                                placeholder=''
                                                inputProps={field}
                                                disabled={!permissionEdit}
                                            />
                                        );
                                    }}
                                />
                            </div>

                            {fields.map((field, index) => (
                                <div key={field.id} className='projects__editing-input'>
                                    <Controller
                                        name={`projects[${index}].project`}
                                        control={control}
                                        render={({field}) => (
                                            <div className={'projects__input-inner'}>
                                                <SelectComponent
                                                    onChange={field.onChange}
                                                    onBlur={field.onBlur}
                                                    value={projectTypes.find((c) => c.id === field.value)}
                                                    options={getFilteredOptions(index)}
                                                    label={`${
                                                        index > 0
                                                            ? t('all.additional') + ' ' + t('ProjectsModal.typeProjectL')
                                                            : t('ProjectsModal.typeProject')
                                                    }`}
                                                    placeholder=''
                                                    error={errors.projects?.[index]?.project?.message}
                                                    className={`${index > 0 ? 'with-clear' : ''}`}
                                                    inputProps={field}
                                                    disabled={!permissionEdit}
                                                />
                                                {index > 0 && (
                                                    <div
                                                        className={`projects__trash${!permissionEdit ? ' disabled-trash' : ''}`}
                                                        onClick={() => permissionEdit && remove(index)}>
                                                        {<RemoveIcon/>}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    />
                                </div>
                            ))}

                            <div
                                className={`project__dynamic${!permissionEdit ? ' disabled' : ''}`}
                                onClick={() => permissionEdit && append({project: ''})}
                            >
                                <PlusIcon/>
                                <span>{t('ProjectsEditing.add_project_type')}</span>
                            </div>

                            <div className='projects__editing-input'>
                                <Controller
                                    name='country'
                                    control={control}
                                    render={({field}) => (
                                        <SelectComponent
                                            onChange={(e) => {
                                                field.onChange(e.target?.value ?? "");
                                                setValue('city', "", {shouldValidate: true});
                                            }}
                                            onBlur={field.onBlur}
                                            value={field.value || ""}
                                            options={countries}
                                            error={errors.country?.message}
                                            label={t('ProjectsEditing.country')}
                                            placeholder=""
                                            inputProps={field}
                                            disabled={!permissionEdit}
                                        />
                                    )}
                                />
                            </div>

                            <div className='projects__editing-input'>
                                <Controller
                                    name='city'
                                    control={control}
                                    render={({field}) => {
                                        return <SelectComponent
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            value={field.value || ''}
                                            options={filteredCities}
                                            label={t('ProjectsEditing.city')}
                                            placeholder=""
                                            error={errors.city?.message}
                                            inputProps={field}
                                            disabled={!filteredCities.length || !permissionEdit}
                                        />
                                    }}
                                />
                            </div>

                            <div className='projects__editing-input'>
                                <Controller
                                    name='address'
                                    control={control}
                                    render={({field}) => (
                                        <InputMUI
                                            type='text'
                                            fullWidth
                                            className='no-icons'
                                            error={errors.address?.message}
                                            inputProps={field}
                                            label={t('ProjectsEditing.address')}
                                            disabled={!permissionEdit}
                                        />
                                    )}
                                />
                            </div>
                            <div className='projects__editing-autocomplete'>
                                <p>{t('ProjectsEditing.add_employee')}</p>
                                <Controller
                                    name='employee'
                                    control={control}
                                    render={({field}) => (
                                        <AutocompleteMUI
                                            className='no-icons'
                                            text={t('ProjectsEditing.select_from_list')}
                                            array={employeeArr}
                                            error={errors?.employee}
                                            value={field.value}
                                            onChange={field.onChange}
                                            disabled={!permissionEdit}
                                        />
                                    )}
                                />
                            </div>
                            <div className='projects__editing-autocomplete'>
                                <p>{t('ProjectsEditing.add_client')}</p>
                                <Controller
                                    name='clients'
                                    control={control}
                                    render={({field}) => (
                                        <AutocompleteMUI
                                            className='no-icons'
                                            text={t('ProjectsEditing.select_from_list')}
                                            array={clientsArr}
                                            error={errors?.clients}
                                            value={field.value}
                                            onChange={field.onChange}
                                            disabled={!permissionEdit}
                                        />
                                    )}
                                />
                            </div>
                            <div className='projects__editing-submit'>
                                {permissionEdit && (
                                    <ButtonMUI
                                        disabled={isSubmitting || !isValid || !avatarWatch}
                                        loading={isSubmitting}
                                        fullWidth={true}
                                        formAction
                                        onClick={handleSubmit(onSubmit)}
                                    >
                                        {t('ProjectsEditing.save')}
                                    </ButtonMUI>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ProjectsEditing;
